<template>
  <div class="lista-produccion-biomasa card mt-4 w-100">
    <div class="card-body">
      <h2 class="card-title">Lista de Producción de Biomasa</h2>
      <p class="text-muted mb-4">
        Seleccione las unidades de biomasa que están listas para producción y asígneles
        una fecha de lista de producción.
      </p>

      <!-- Toolbar -->
      <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
        <!-- Fecha con icono -->
        <div class="input-group input-group-sm me-auto" style="max-width: 220px;">
          <span class="input-group-text bg-white border-end-0">
            <font-awesome-icon icon="fas fa-calendar-alt" />
          </span>
          <input
            v-model="productionDate"
            type="date"
            class="form-control form-control-sm border-start-0"
          />
        </div>

        <!-- Botón marcar -->
        <button
          class="btn btn-success btn-sm"
          :disabled="!anySelected"
          @click="markAsProduccion"
        >
          <font-awesome-icon icon="fas fa-check-circle" class="me-1" />
          Marcar listas ({{ selected.length }})
        </button>

        <!-- Botón seleccionar todo -->
        <button
          class="btn btn-outline-secondary btn-sm"
          @click="toggleAll"
        >
          <font-awesome-icon
            :icon="allSelected ? ['fas','check-square'] : ['far','square']"
            class="me-1"
          />
          {{ allSelected ? 'Deseleccionar Todo' : 'Seleccionar Todo' }}
        </button>
      </div>

      <!-- Tabla -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
      <div v-else>
        <div class="table-responsive">
          <table class="table table-hover table-sm align-middle">
            <thead class="table-light">
              <tr>
                <th style="width: 1rem;"></th>
                <th>ID</th>
                <th>Tipo</th>
                <th>Cantidad</th>
                <th>Unidad</th>
                <th>Fecha Cosecha</th>
                <th>Observaciones</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="b in paginatedBiomasa"
                :key="b.id"
                :class="{ 'table-active': selected.includes(b.id) }"
              >
                <td>
                  <input type="checkbox" :value="b.id" v-model="selected" />
                </td>
                <td>{{ b.id }}</td>
                <td>{{ b.tipo_biomasa }}</td>
                <td>{{ b.peso.toFixed(2) }}</td>
                <td>{{ b.unidad_medida }}</td>
                <td>{{ formatDate(b.fecha_cosecha) }}</td>
                <td>{{ b.observaciones || '—' }}</td>
              </tr>
              <tr v-if="!biomasaList.length">
                <td colspan="7" class="text-center text-muted">
                  No hay biomasa disponible para este evento.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginación -->
        <nav v-if="totalPages > 1" class="mt-2">
          <ul class="pagination pagination-sm justify-content-center mb-0">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link">Anterior</button>
            </li>
            <li
              v-for="p in visiblePages"
              :key="p"
              class="page-item"
              :class="{ active: currentPage === p }"
            >
              <button class="page-link" @click="goToPage(p)">{{ p }}</button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <button class="page-link" @click="nextPage">Siguiente</button>
            </li>
          </ul>
        </nav>

        <!-- Mensaje de éxito/error -->
        <transition name="fade">
          <div v-if="message" class="alert alert-info mt-3">
            {{ message }}
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios';
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'ListaProduccionBiomasa',
  components: { FontAwesomeIcon },
  props: {
    proyecto_evento_id: { type: Number, required: true }
  },
  data() {
    return {
      biomasaList: [],
      selected: [],
      productionDate: new Date().toISOString().substr(0, 10),
      loading: false,
      message: '',
      currentPage: 1,
      pageSize: 10
    };
  },
  computed: {
    ...mapGetters(['getClubActual']),
    club_id() { return this.getClubActual.id; },
    anySelected() { return this.selected.length > 0; },
    allSelected: {
      get() { return this.biomasaList.length && this.selected.length === this.biomasaList.length; },
      set(val) { this.selected = val ? this.biomasaList.map(b => b.id) : []; }
    },
    paginatedBiomasa() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.biomasaList.slice(start, start + this.pageSize);
    },
    totalPages() { return Math.ceil(this.biomasaList.length / this.pageSize); },
    visiblePages() {
      const total = this.totalPages;
      const pages = [];
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(total, start + 4);
      if (end - start < 4) start = Math.max(1, end - 4);
      for (let p = start; p <= end; p++) pages.push(p);
      return pages;
    }
  },
  methods: {
    async fetchBiomasa() {
      this.loading = true;
      try {
        const res = await axios.get('/api/lista_biomasa_not_lista_produccion', {
          params: { club_id: this.club_id, proyecto_evento_id: this.proyecto_evento_id }
        });
        this.biomasaList = res.data || [];
      } finally {
        this.loading = false;
      }
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString() : '-';
    },
    toggleAll() { this.allSelected = !this.allSelected; },
    prevPage() { if (this.currentPage > 1) this.currentPage--; },
    nextPage() { if (this.currentPage < this.totalPages) this.currentPage++; },
    goToPage(p) { this.currentPage = p; },
    async markAsProduccion() {

      if (!this.anySelected) return;
      try {
        // Resumen y confirmación
          const resumen = [
            `Vas a marcar ${this.selected.length} ítem${this.selected.length > 1 ? 's' : ''} como listas para producción.`,
            `Fecha de producción: ${this.productionDate}.`,
            'Esta acción NO tiene vuelta atrás y estas biomasas pasarán a ser MATERIAS PRIMAS.'
          ].join('\n');

          if (!window.confirm(resumen)) {
            return;
          }
        await axios.put(
          '/api/biomasa_actualizar_lista_produccion',
          {
            biomasa_ids: this.selected,
            fecha_lista_produccion: this.productionDate,
            proyecto_evento_id: this.proyecto_evento_id
          },
          { params: { club_id: this.club_id } }
        );
        this.message = `Se marcaron ${this.selected.length} ítems como listas para producción.`;
        setTimeout(() => (this.message = ''), 4000);
        this.fetchBiomasa();
        this.selected = [];
      } catch {
        this.message = 'Error al marcar producción.';
        setTimeout(() => (this.message = ''), 4000);
      }
    }
  },
  mounted() {
    this.fetchBiomasa();
  }
};
</script>

<style scoped>
.lista-produccion-biomasa {
  margin: 0;
}
.table-hover tbody tr.table-active {
  background-color: #d1ecf1;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity .3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
